import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import { Lock, ShieldOff } from 'lucide-react'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { useUIStore } from '@/store/useUIStore'
import { adminNav, adminNavPermissions, filterAdminNav, flattenNav } from './navConfig'
import { useAccountRestrictions } from '@/shared/accessControl'
import { useCan } from '@/shared/permissions'
import { useMemo } from 'react'

const seg3 = (p: string) => p.split('/').filter(Boolean).slice(0, 3).join('/')
const leaves = flattenNav(adminNav).filter(i => !i.children?.length)

export function AdminLayout() {
  const sidebarOpen = useUIStore(s => s.sidebarOpen)
  const { hiddenPaths } = useAccountRestrictions()
  const can = useCan()
  const { pathname } = useLocation()

  // Restricted accounts lose some nav entries — drop them (and any parent that
  // is left with no children) before the sidebar renders. On top of that, hide every
  // entry the admin has no permission for.
  const sections = useMemo(() => {
    const base = !hiddenPaths.length ? adminNav : (() => {
      const hidden = new Set(hiddenPaths)
      return adminNav
        .map(sec => ({
          ...sec,
          items: sec.items
            .map(item => item.children
              ? { ...item, children: item.children.filter(c => !hidden.has(c.path)) }
              : item)
            .filter(item => !hidden.has(item.path) && !(item.children && item.children.length === 0)),
        }))
        .filter(sec => sec.items.length > 0)
    })()
    return filterAdminNav(base, can)
  }, [hiddenPaths, can])

  // Direct-URL guard: the page is blocked when every menu entry that owns this URL is one the
  // admin lacks permission for. URLs that no menu entry owns are left alone.
  const denied = useMemo(() => {
    let owners = leaves.filter(i => i.path === pathname)
    if (!owners.length && pathname.split('/').filter(Boolean).length >= 3) {
      owners = leaves.filter(i => seg3(i.path) === seg3(pathname))
    }
    return owners.length > 0 && owners.every(i => {
      const req = adminNavPermissions[i.label]
      return !!req && !can(req)
    })
  }, [pathname, can])

  // Read-only notice: the admin may open this page (view) but holds no add/change/delete permission for
  // its section. Pages that are read-only by nature (dashboard, reports) never get the notice.
  const readOnly = useMemo(() => {
    let owners = leaves.filter(i => i.path === pathname)
    if (!owners.length && pathname.split('/').filter(Boolean).length >= 3) {
      owners = leaves.filter(i => seg3(i.path) === seg3(pathname))
    }
    const viewable = owners.filter(i => {
      const req = adminNavPermissions[i.label]
      return !!req && !NATURALLY_READ_ONLY.has(i.label) && can(req)
    })
    return viewable.length > 0 && viewable.every(i => !can(writeKeysFor(adminNavPermissions[i.label])))
  }, [pathname, can])

  const firstAllowed = flattenNav(sections).find(i => !i.children?.length)?.path

  if (denied && pathname === '/dashboard' && firstAllowed && firstAllowed !== '/dashboard') {
    return <Navigate to={firstAllowed} replace />
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar sections={sections} open={sidebarOpen}/>
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Header/>
        <main id="main-scroll-area" className="flex-1 overflow-y-auto overflow-x-hidden">
          <div className="p-3 sm:p-5">
            {readOnly && !denied && <ReadOnlyNotice />}
            {denied ? <NoAccess to={firstAllowed} /> : <Outlet/>}
          </div>
        </main>
      </div>
    </div>
  )
}

// Pages with nothing to add/edit/delete — a "read-only" notice would be noise there.
const NATURALLY_READ_ONLY = new Set([
  'Dashboard', 'Reports & Analytics', 'Booking Report', 'Revenue Report', 'Trainer Report',
  'Student Report', 'School Report', 'Booking Tracking', 'Profile Change Logs',
])

/** The write permissions that go with a read requirement: 'GET /admin/booking/*' → POST/PUT/PATCH/DELETE on the same section. */
function writeKeysFor(read: string | string[] | undefined): string[] {
  const out: string[] = []
  for (const r of Array.isArray(read) ? read : read ? [read] : []) {
    const m = /^GET (\/(?:v2\/)?admin\/[^/*]+)/.exec(r)
    if (m) ['POST', 'PUT', 'PATCH', 'DELETE'].forEach(v => out.push(`${v} ${m[1]}/*`))
  }
  return out
}

function ReadOnlyNotice() {
  return (
    <div role="status" className="mb-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <Lock size={16} className="mt-0.5 shrink-0" />
      <p>
        <b>Read-only access.</b> You can view this page, but your role doesn’t have edit or full access here,
        so add, edit and delete actions are hidden. Ask an administrator if you need more access.
      </p>
    </div>
  )
}

function NoAccess({ to }: { to?: string }) {
  return (
    <div className="max-w-md mx-auto text-center py-24 space-y-4">
      <ShieldOff className="mx-auto text-gray-300" size={44} />
      <h2 className="text-lg font-bold text-gray-900">You don’t have access to this page</h2>
      <p className="text-sm text-gray-500">Your role doesn’t include this area. Ask an administrator to give you access.</p>
      {to && <Link to={to} className="inline-block text-sm font-medium text-brand-600 hover:underline">Go to a page you can open</Link>}
    </div>
  )
}
